import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import Swiper from 'swiper';
import { finishingContent } from './Finishing.content';
import arrow from '../HalfSlider/assets/arrow.svg';
import styles from './Finishing.module.sass';
import 'swiper/dist/css/swiper.min.css';
import cloud1 from './assets/cloud-1.webp';
import cloud2 from './assets/cloud-2.webp';

export function Finishing() {
  const { title, slides } = finishingContent;
  const containerRef = useRef(null);
  const swiperRef = useRef(null);
  const [viewport, setViewport] = useState('desktop');
  const [activeTab, setActiveTab] = useState('comfort');
  const [activePointIndex, setActivePointIndex] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const currentSlides = useMemo(() => slides[activeTab] || [], [slides, activeTab]);

  useEffect(() => {
    const updateViewport = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setViewport('mobile');
      } else if (window.matchMedia('(max-width: 1024px)').matches) {
        setViewport('tablet');
      } else {
        setViewport('desktop');
      }
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    if (swiperRef.current) {
      swiperRef.current.destroy(true, true);
      swiperRef.current = null;
    }

    swiperRef.current = new Swiper(containerRef.current, {
      slidesPerView: 1,
      speed: 700,
      autoHeight: true,
    });

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, [activeTab]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [viewport, activeTab]);

  useEffect(() => {
    setActivePointIndex(0);
    setUserInteracted(false);
  }, [activeTab]);

  useEffect(() => {
    if (userInteracted || currentSlides.length === 0) return;

    const currentSlide = currentSlides[swiperRef.current?.activeIndex || 0];
    if (!currentSlide || !currentSlide.points || currentSlide.points.length === 0) return;

    const interval = setInterval(() => {
      setActivePointIndex((prevIndex) => {
        const maxIndex = currentSlide.points.length - 1;
        return prevIndex >= maxIndex ? 0 : prevIndex + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [userInteracted, currentSlides, activeTab]);

  const handlePrev = useCallback(() => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (swiperRef.current) swiperRef.current.slideNext();
  }, []);

  return (
    <section className={styles.finishing}>
      <div className={styles.finishing__clouds} aria-hidden>
        <img src={cloud1} alt="" className={styles.finishing__cloud1} />
        <img src={cloud2} alt="" className={styles.finishing__cloud2} />
      </div>
      <div className={styles.finishing__container}>
        <h2 className={styles.finishing__title}>
          {title.map((seg, i) => (
            <React.Fragment key={i}>
              {seg.accent ? (
                <span className={styles.finishing__titleAccent}>{seg.text}</span>
              ) : (
                seg.text
              )}
              {i < title.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>

        <div className={styles.finishing__tabs}>
          <button
            type="button"
            className={`${styles.finishing__tabBtn} ${
              activeTab === 'comfort' ? styles['finishing__tabBtn--active'] : ''
            }`}
            onClick={() => setActiveTab('comfort')}
          >
            комфорт
          </button>
          <button
            type="button"
            className={`${styles.finishing__tabBtn} ${
              activeTab === 'standard' ? styles['finishing__tabBtn--active'] : ''
            }`}
            onClick={() => setActiveTab('standard')}
          >
            стандарт
          </button>
        </div>

        <div className={styles.finishing__nav}>
          <button
            className={styles.finishing__navBtn}
            onClick={handlePrev}
            aria-label="Предыдущий слайд"
          >
            <img src={arrow} alt="" className={styles.finishing__navIcon} />
          </button>
          <button
            className={styles.finishing__navBtn}
            onClick={handleNext}
            aria-label="Следующий слайд"
          >
            <img
              src={arrow}
              alt=""
              className={`${styles.finishing__navIcon} ${styles['finishing__navIcon--right']}`}
            />
          </button>
        </div>

        <div
          key={activeTab}
          className={`swiper-container ${styles.finishing__swiper}`}
          ref={containerRef}
        >
          <div className="swiper-wrapper">
            {currentSlides.map((slide, index) => (
              <div key={`${activeTab}-${slide.id}-${index}`} className="swiper-slide">
                <div
                  className={`${styles.finishing__slide}${
                    slide.tallTouch ? ` ${styles['finishing__slide--tall']}` : ''
                  }`}
                >
                  {slide.image && (
                    <picture>
                      <source
                        media="(max-width: 768px)"
                        srcSet={slide.imageMobile || slide.image}
                      />
                      <source
                        media="(max-width: 1024px)"
                        srcSet={slide.imageTablet || slide.image}
                      />
                      <img
                        src={slide.image}
                        alt=""
                        className={styles.finishing__slideBg}
                        decoding="async"
                      />
                    </picture>
                  )}

                  <div className={styles.finishing__slideOverlay} />

                  {slide.points.map((point, pointIndex) => {
                    const isNarrow = viewport === 'mobile' || viewport === 'tablet';
                    const x = isNarrow && point.xTablet !== undefined ? point.xTablet : point.x;
                    const y = isNarrow && point.yTablet !== undefined ? point.yTablet : point.y;
                    const isActive = !userInteracted && pointIndex === activePointIndex;

                    return (
                      <div
                        key={point.id}
                        className={`${styles.finishing__point} ${
                          isActive ? styles['finishing__point--active'] : ''
                        }`}
                        style={{ left: `${x}%`, top: `${y}%` }}
                        onMouseEnter={() => setUserInteracted(true)}
                      >
                        <div className={styles.finishing__pointMarker}>
                          <span className={styles.finishing__pointDot} />
                        </div>
                        <span className={styles.finishing__pointLabel}>{point.label}</span>
                        <div
                          className={`${styles.finishing__pointTooltip}${
                            point.tooltipPosition
                              ? ` ${styles[`finishing__pointTooltip--${point.tooltipPosition}`]}`
                              : ''
                          }`}
                        >
                          <p>
                            {point.tooltip.map((seg, i) => (
                              <React.Fragment key={i}>
                                {seg.accent ? (
                                  <span
                                    className={
                                      styles[
                                        'finishing__pointTooltip__pointTooltip__pointTooltipAccent'
                                      ]
                                    }
                                  >
                                    {seg.text}
                                  </span>
                                ) : (
                                  seg.text
                                )}
                              </React.Fragment>
                            ))}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  <div className={styles.finishing__slideInfo}>
                    <h3 className={styles.finishing__slideName}>
                      {slide.name.map((seg, i) => (
                        <React.Fragment key={i}>
                          {seg.accent ? (
                            <span className={styles.finishing__slideNameAccent}>
                              {seg.text}
                              <br />
                            </span>
                          ) : (
                            seg.text
                          )}
                        </React.Fragment>
                      ))}
                    </h3>
                    <p className={styles.finishing__slideDesc}>
                      {slide.description.map((seg, i) => (
                        <React.Fragment key={i}>
                          {seg.accent ? (
                            <span className={styles.finishing__slideDescAccent}>
                              {seg.text}
                              <br />
                            </span>
                          ) : (
                            seg.text
                          )}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
